import React, { FC } from 'react';

type PlaceholderMap = {
  [key: string]: JSX.Element;
};

type Props = {
  text: string;
  placeholders: PlaceholderMap;
};

/**
 * RenderLocalesWithComponents is a helper component that replaces placeholder strings
 * in the provided text with corresponding React components.
 *
 * It splits the input text using a regular expression to match placeholders (e.g., "!{PLACEHOLDER}!")
 * and renders the respective React elements from the `placeholders` mapping.
 * If a part of the text does not match any key in the `placeholders` object, it is rendered as plain text.
 *
 * Props for the RenderLocalesWithComponents component.
 *
 * The props for the component, including the text and the placeholders mapping.
 * @param text - The text string that may contain placeholders formatted as "!{PLACEHOLDER}!".
 * @param placeholders - An object mapping placeholder keys to React elements.
 *
 * @example
 * const placeholders: PlaceholderMap = {
 *   '!{PRICE}!': <Typography variant="body1">$100</Typography>
 * };
 * @returns A React fragment containing the rendered output with replaced components.
 *
 *
 * @example
 * import React from 'react';
 * import Typography from './Typography';
 * import { RenderLocalesWithComponents } from './RenderLocalesWithComponents';
 *
 * const text = "The price is !{PRICE}!";
 * const placeholders = {
 *   '!{PRICE}!': <Typography variant="body1">$100</Typography>
 * };
 *
 * const SampleComponent: React.FC = () => (
 *   <div>
 *     <RenderLocalesWithComponents text={text} placeholders={placeholders} />
 *   </div>
 * );
 *
 * export default SampleComponent;
 *
 */
export const RenderLocalesWithComponents: FC<Props> = ({ text, placeholders }) => {
  const parts = text.split(/(!\{\w+\}!)/g);

  return (
    <>
      {parts.map((part, index) =>
        placeholders[part] ? (
          // eslint-disable-next-line react/no-array-index-key
          <React.Fragment key={`${index}`}>{placeholders[part]}</React.Fragment>
        ) : (
          // eslint-disable-next-line react/no-array-index-key
          <React.Fragment key={`${index}`}>{part}</React.Fragment>
        ),
      )}
    </>
  );
};
